// Reference-data overlays: facilities, MATCOM WATCH™ cameras, live MD CHART /
// PA 511 traffic cameras and the Baltimore 911 CFS heat layer.
// Split out of DispatchMap.jsx (refactor only — logic unchanged).
import React, { useEffect, useState } from "react";
import { Marker, Popup, CircleMarker, GeoJSON, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import client from "@/lib/api";
import { valid } from "./mapShared";

// AI-seeded MD/PA facilities as small square markers, colored by type. Fetched once
// when the layer is first switched on (reference data — changes rarely).
const FAC_COLOR = { hospital: "#EF4444", fire_station: "#F97316", police_station: "#3B82F6",
  ems_station: "#10B981", school: "#F59E0B", federal_building: "#A78BFA" };
function facilityIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:12px;height:12px;background:${color};border:2px solid #0d1017;box-shadow:0 0 4px ${color};transform:rotate(45deg)"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
  });
}
export function FacilityLayer() {
  const [facs, setFacs] = useState([]);
  useEffect(() => { client.get("/facilities").then((r) => setFacs(r.data.facilities)).catch(() => {}); }, []);
  return facs.filter((f) => valid(f.lat, f.lng)).map((f) => (
    <Marker key={f.id} position={[f.lat, f.lng]} icon={facilityIcon(FAC_COLOR[f.facility_type] || "#9CA3AF")}>
      <Popup>
        <div className="font-mono text-xs">
          <b>{f.name}</b><br />
          {(f.facility_type || "").replace(/_/g, " ").toUpperCase()}<br />
          {f.address}{f.city ? `, ${f.city}` : ""} {f.state}<br />
          {f.phone && <a href={`tel:${f.phone.replace(/[^\d+]/g, "")}`}>{f.phone}</a>}
        </div>
      </Popup>
    </Marker>
  ));
}

// MATCOM WATCH™ camera pins. Fetched ONCE when the layer is switched on — static
// markers only (no video on the map), so zero impact on map/polling performance.
const CAM_COLOR = { online: "#22C55E", degraded: "#F59E0B", offline: "#EF4444" };
export function cameraIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:22px;height:22px;border-radius:6px;background:#0d1017;border:2px solid ${color};box-shadow:0 0 6px ${color};display:flex;align-items:center;justify-content:center">
      <svg viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.4" width="13" height="13"><path d="M2 8l12-3 1 4-12 3z"/><path d="M13 9.5l7-2v6l-5-1"/><path d="M6 12v5h3"/></svg></div>`,
    iconSize: [24, 24], iconAnchor: [12, 12],
  });
}
export function CameraLayer() {
  const [cams, setCams] = useState([]);
  useEffect(() => { client.get("/watch/cameras").then((r) => setCams(r.data.cameras || [])).catch(() => {}); }, []);
  return cams.filter((c) => valid(c.lat, c.lng)).map((c) => (
    <Marker key={c.cam_id} position={[c.lat, c.lng]} icon={cameraIcon(CAM_COLOR[c.status] || "#9CA3AF")}>
      <Popup>
        <div className="font-mono text-xs">
          <b>{c.cam_id}</b> — {c.name}<br />
          {c.address}<br />
          STATUS: <span style={{ color: CAM_COLOR[c.status] }}>{(c.status || "").toUpperCase()}</span>
          {c.feed_url
            ? <><br /><a href={c.feed_url} target="_blank" rel="noreferrer">OPEN LIVE FEED ↗</a></>
            : <><br />MATCOM WATCH™ · DEMO FEED</>}
          {c.linked_calls?.length ? <><br />Linked: {c.linked_calls.slice(-3).map((l) => l.cc).join(", ")}</> : null}
        </div>
      </Popup>
    </Marker>
  ));
}

// LIVE Maryland CHART traffic cameras (public feed) — fetched once per toggle,
// server caches upstream 10 min, so zero performance impact. PURPLE TRIANGLE pins.
function mdTriangleIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-bottom:16px solid ${color};filter:drop-shadow(0 0 4px ${color})"></div>`,
    iconSize: [18, 16], iconAnchor: [9, 8],
  });
}
export function LiveCameraLayer() {
  const [cams, setCams] = useState([]);
  useEffect(() => { client.get("/watch/live-cameras").then((r) => setCams(r.data.cameras || [])).catch(() => {}); }, []);
  return cams.filter((c) => valid(c.lat, c.lng)).map((c) => (
    <Marker key={c.id} position={[c.lat, c.lng]} icon={mdTriangleIcon(c.status === "online" ? "#A855F7" : "#6B7280")}>
      <Popup>
        <div className="font-mono text-xs">
          <b>MD CHART</b> — {c.name}<br />
          {c.description}<br />
          {c.category} · <span style={{ color: c.status === "online" ? "#A855F7" : "#9CA3AF" }}>{c.status.toUpperCase()}</span><br />
          {c.video_url && <a href={c.video_url} target="_blank" rel="noreferrer"><b>OPEN LIVE FEED ↗</b></a>}
        </div>
      </Popup>
    </Marker>
  ));
}

// LIVE Pennsylvania 511PA traffic cameras (statewide, public feed) — cached 15 min
// server-side. Popup loads ONE live snapshot image only when opened.
export function PaCameraLayer() {
  const [cams, setCams] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { client.get("/watch/pa-cameras").then((r) => setCams(r.data.cameras || [])).catch(() => {}); }, []);
  return cams.filter((c) => valid(c.lat, c.lng)).map((c) => (
    <Marker key={c.id} position={[c.lat, c.lng]} icon={cameraIcon("#FBBF24")}
      eventHandlers={{ popupopen: () => setOpenId(c.id), popupclose: () => setOpenId(null) }}>
      <Popup>
        <div className="font-mono text-xs" style={{ maxWidth: 240 }}>
          <b>PA 511</b> — {c.name}<br />
          {c.roadway} {c.direction && c.direction !== "Unknown" ? `· ${c.direction}` : ""}<br />
          {c.image_url && openId === c.id && (
            <img src={`${c.image_url}?t=${Date.now()}`} alt={c.name} style={{ width: "100%", marginTop: 4, border: "1px solid #2C2D35" }} />
          )}
          {c.image_url && <a href={c.image_url} target="_blank" rel="noreferrer"><b>OPEN LIVE SNAPSHOT ↗</b></a>}
        </div>
      </Popup>
    </Marker>
  ));
}

// Baltimore 911 CFS overlay (helps MDT units plan patrol routes) — heat cells plus
// the most recent calls as TYPE ICONS (gun = shooting/ShotSpotter, fighters = fight/
// assault, body = homicide…) with popups describing each call.
export const BALT_ICON = { shooting: "🔫", homicide: "💀", fight: "🤼", armed: "🔪", robbery: "💰", accident: "🚗", medical: "🚑", other: "⚠️" };
// Icon-filter categories (order = display order of the filter pills)
export const BALT_CATS = [
  ["shooting", "SHOOT"], ["homicide", "HOMIC"], ["fight", "FIGHT"], ["armed", "ARMED"],
  ["robbery", "ROBB"], ["medical", "MED"], ["accident", "ACC"], ["other", "OTHER"],
];
export const ALL_BALT_CATS = BALT_CATS.map(([k]) => k);
export function loadBaltCats() {
  try {
    const raw = localStorage.getItem("map_balt_cats");
    if (!raw) return ALL_BALT_CATS;
    const arr = JSON.parse(raw).filter((k) => ALL_BALT_CATS.includes(k));
    return arr.length ? arr : ALL_BALT_CATS;
  } catch { return ALL_BALT_CATS; }
}
function baltIcon(cat, pri) {
  return L.divIcon({
    className: "",
    html: `<div style="font-size:15px;line-height:1;filter:drop-shadow(0 0 3px ${pri === "Emergency" ? "#EF4444" : "#F59E0B"})">${BALT_ICON[cat] || "⚠️"}</div>`,
    iconSize: [18, 18], iconAnchor: [9, 9],
  });
}
export function BaltHeatLayer({ cats = ALL_BALT_CATS }) {
  const [cells, setCells] = useState([]);
  const [pts, setPts] = useState([]);
  useEffect(() => {
    client.get("/baltimore/heat").then((r) => setCells(r.data.cells || [])).catch(() => {});
    client.get("/baltimore/points", { params: { limit: 500 } }).then((r) => setPts(r.data.points || [])).catch(() => {});
  }, []);
  const max = Math.max(1, ...cells.map((c) => c.count));
  const shown = pts.filter((p) => cats.includes(p.category || "other"));
  return (
    <>
      {cells.map((c, i) => (
        <CircleMarker key={`h${i}`} center={[c.lat, c.lng]} radius={3 + 12 * (c.count / max)}
          pathOptions={{ color: "#F97316", fillColor: "#F97316", fillOpacity: 0.2, weight: 0.5 }} />
      ))}
      {shown.map((p, i) => (
        <Marker key={`p${i}`} position={[p.lat, p.lng]} icon={baltIcon(p.category, p.priority)}>
          <Popup>
            <div className="font-mono text-xs">
              <b>{BALT_ICON[p.category]} {p.description}</b><br />
              {p.priority} · {p.police_district} District<br />
              {p.address || ""}{p.neighborhood ? ` · ${p.neighborhood}` : ""}<br />
              {p.ts ? new Date(p.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : ""}<br />
              <span style={{ opacity: 0.7 }}>(position district-level approximate)</span>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}

// Baltimore Police Department district boundaries — official BPD ArcGIS layer
// (redistricting effective July 2023), served from the backend. Labelled polygons.
const BPD_COLORS = { CD: "#3B82F6", SED: "#10B981", ED: "#F59E0B", NED: "#A78BFA", ND: "#22D3EE", NWD: "#F97316", WD: "#EF4444", SWD: "#EC4899", SD: "#84CC16" };
export function BpdDistrictLayer() {
  const [fc, setFc] = useState(null);
  useEffect(() => { client.get("/geo/layers/bpd-districts").then((r) => setFc(r.data)).catch(() => {}); }, []);
  if (!fc) return null;
  return (
    <GeoJSON key="bpd" data={fc}
      style={(f) => { const c = BPD_COLORS[f.properties.Dist_Abbr] || "#9CA3AF"; return { color: c, weight: 2, fillColor: c, fillOpacity: 0.08, dashArray: "4 3" }; }}
      onEachFeature={(f, layer) => {
        const p = f.properties;
        layer.bindTooltip(`<div style="font-family:monospace;font-size:10px;font-weight:700;color:${BPD_COLORS[p.Dist_Abbr] || "#fff"}">BPD ${String(p.District).toUpperCase()} · #${p.Number}</div>`, { permanent: true, direction: "center", className: "bpd-label" });
        layer.bindPopup(`<div class="font-mono text-xs"><b>Baltimore Police Department</b><br/>${p.District} District (#${p.Number} · ${p.Dist_Abbr})<br/><span style="opacity:.7">Boundaries effective July 2023 — baltimorepolice.org/redistricting-map</span></div>`);
      }} />
  );
}

// Pennsylvania police jurisdictions — PCCD statewide layer (municipal / regional /
// contracted / State-Police-only). Colour = service type; hover shows the agency.
const PA_COLORS = { "Municipal Police Force": "#10B981", "Regional Police Service": "#A78BFA", "Police Service contracted from another municipality": "#F59E0B", "State Police coverage only": "#6B7280" };
export function PaPoliceLayer() {
  const [fc, setFc] = useState(null);
  useEffect(() => { client.get("/geo/layers/pa-police").then((r) => setFc(r.data)).catch(() => {}); }, []);
  if (!fc) return null;
  return (
    <GeoJSON key="pa-police" data={fc}
      style={(f) => { const c = PA_COLORS[f.properties.POLICE_SER] || "#9CA3AF"; return { color: c, weight: 1, fillColor: c, fillOpacity: 0.07 }; }}
      onEachFeature={(f, layer) => {
        const p = f.properties;
        const who = p.POLICE_SER === "State Police coverage only" ? "Pennsylvania State Police" : `${p.Jurisdicti || p.MUNICIPALI} Police`;
        layer.bindTooltip(`<div style="font-family:monospace;font-size:10px"><b>${who}</b><br/>${p.MUNICIPALI} · ${p.County} Co.</div>`, { sticky: true });
        layer.bindPopup(`<div class="font-mono text-xs"><b>${who}</b><br/>${p.MUNICIPALI}, ${p.County} County<br/>${p.POLICE_SER}${p.POLICE_FUL ? `<br/>${p.POLICE_FUL} FT · ${p.POLICE_PAR} PT officers` : ""}<br/><span style="opacity:.7">PA PCCD Police Jurisdictions</span></div>`);
      }} />
  );
}

// "Show on map" from intake: highlights the jurisdiction polygon containing the
// pin (BPD district / PA municipality) and zooms to it. One-shot via sessionStorage.
export function FocusJurisdiction() {
  const map = useMap();
  const [shape, setShape] = useState(null);
  useEffect(() => {
    const raw = sessionStorage.getItem("map_focus_shape");
    if (!raw) return;
    sessionStorage.removeItem("map_focus_shape");
    window.__mapFocusPending = true;
    let f; try { f = JSON.parse(raw); } catch { window.__mapFocusPending = false; return; }
    const go = (fn) => setTimeout(() => { fn(); setTimeout(() => { window.__mapFocusPending = false; }, 3000); }, 400);
    client.get("/ali/jurisdiction-shape", { params: { lat: f.lat, lng: f.lng } }).then((r) => {
      setShape({ ...r.data, pin: f });
      const layer = L.geoJSON(r.data);
      go(() => map.fitBounds(layer.getBounds(), { padding: [30, 30], animate: false }));
    }).catch(() => go(() => map.setView([f.lat, f.lng], 15, { animate: false })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!shape) return null;
  return (
    <>
      <GeoJSON key={shape.properties.label} data={shape} style={{ color: "#22D3EE", weight: 3, fillColor: "#22D3EE", fillOpacity: 0.12 }}>
        <Tooltip permanent direction="center" className="bpd-label"><span style={{ fontFamily: "monospace", fontSize: 10, fontWeight: 700, color: "#67E8F9" }}>{shape.properties.label}</span></Tooltip>
      </GeoJSON>
      <CircleMarker center={[shape.pin.lat, shape.pin.lng]} radius={7} pathOptions={{ color: "#fff", fillColor: "#22D3EE", fillOpacity: 1, weight: 2 }}>
        <Popup><div className="font-mono text-xs"><b>{shape.pin.address}</b><br />{shape.properties.label}</div></Popup>
      </CircleMarker>
    </>
  );
}

// PSP barracks (PASDA station points) coloured by troop, with troop boundaries faint.
const TROOP_COLORS = { A: "#EF4444", B: "#F97316", C: "#F59E0B", D: "#84CC16", E: "#10B981", F: "#14B8A6", G: "#22D3EE", H: "#3B82F6", J: "#6366F1", K: "#A78BFA", L: "#EC4899", M: "#F43F5E", N: "#FBBF24", P: "#34D399", R: "#60A5FA", T: "#9CA3AF", HQ: "#FFFFFF" };
function pspIcon(color) {
  return L.divIcon({ className: "", html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #0d1017;box-shadow:0 0 6px ${color};display:flex;align-items:center;justify-content:center;font:700 8px monospace;color:#0d1017">P</div>`, iconSize: [18, 18], iconAnchor: [9, 9] });
}
export function PspStationLayer() {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/geo/layers/psp-stations").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <>
      <GeoJSON key="psp-troops" data={d.troops} style={(f) => ({ color: TROOP_COLORS[(f.properties.troop || "").toUpperCase()] || "#9CA3AF", weight: 1, fillOpacity: 0.04, dashArray: "2 4" })}
        onEachFeature={(f, layer) => layer.bindTooltip(`<span style="font-family:monospace;font-size:10px;font-weight:700;color:${TROOP_COLORS[(f.properties.troop || "").toUpperCase()] || "#fff"}">PSP TROOP ${f.properties.troop}</span>`, { permanent: true, direction: "center", className: "bpd-label" })} />
      {d.stations.filter((s) => valid(s.lat, s.lng)).map((s) => (
        <Marker key={s.name} position={[s.lat, s.lng]} icon={pspIcon(TROOP_COLORS[s.troop] || "#9CA3AF")}>
          <Tooltip direction="top" offset={[0, -8]}><span style={{ fontFamily: "monospace", fontSize: 10 }}>PSP {s.name}{s.phone ? ` · ${s.phone}` : ""}</span></Tooltip>
          <Popup>
            <div className="font-mono text-xs">
              <b>Pennsylvania State Police — {s.name}</b><br />
              {s.address}<br />
              {s.phone && <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`}><b>{s.phone}</b></a>}<br />
              <span style={{ opacity: 0.7 }}>PASDA / PSP station layer</span>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}
